// BRIEF 108 §4 — the WhatsApp import card. WRITTEN ONCE, RENDERED BY BOTH
// VIEWS, the way `CaseEmailsPane` already is: `kind` selects the URL base and
// the permission scope, and nothing else differs between a case and a
// collective case.
//
// The chrome is the Emails card's, deliberately — this sits immediately beside
// it and a second visual language for the same kind of work would be a second
// thing to learn.
//
// ── WHAT THE LIST SHOWS, AND WHAT IT DELIBERATELY DOES NOT ──────────────────
//
// ONLY MESSAGES ABOVE THE FREE-WORD FLOOR. Everything at or below it is
// unbillable by definition — not shown, not counted, not selectable. In the
// measured 526-message export that is 298 rows, mostly media placeholders and
// one-word replies, and a review list that showed them would be a list nobody
// could read. THE FILTER RUNS ON THE SERVER, so those messages never reach this
// browser at all; the count of what was withheld is reported so the number is
// answerable rather than merely absent.
//
// THE ARCHIVE IS THE OPPOSITE. Its PDF carries the WHOLE conversation,
// including every message this list filtered out: the archive is the memory
// record, the list is only about billing.
//
// ── THE SWAP IS FREE, BECAUSE BOTH DIRECTIONS ARE ALREADY PRICED ────────────
//
// Every row arrives with `minutesIn` AND `minutesOut` (and, since 194D E.4,
// `amountIn` AND `amountOut` at the viewer's own rate). Flipping who the other
// party is therefore re-prices the whole list with no round trip and — the part
// that matters — with no formula on this side: the drawer PICKS a number, it
// never computes one.
//
// ── 194D §C.0 / §E — THE LIST LIVES IN THE DRAWER NOW ──────────────────────
//
// Laurent, on the pane as it stood: "messy because opens in CV/CCV a giant
// list + one can't close that list without faking to choose another file".
// The list, the counters, the billing strip, the other-party question and the
// readout lines MOVED to `MessagesDrawer.tsx` (the brief's one new file);
// this file keeps the types the drawer reads, the one row, and the mount.
//
// ── 194D §C — THE PANE KEEPS ONE ROW, AND THE ROW IS A COMPONENT ────────────
//
// `Importer d'un fichier :` · [the file field — « Désigner le fichier », then
// the chosen file's name with its suffix, cut at the end] · [Importer], the
// grammar of the e-mails pane's rows (B.3: title — a field that fills the
// middle — the button on the right edge). `WhatsappImportRow` is exported at
// module scope, hookless, so the messages drawer's first row (§E.1) renders
// the SAME row live — the way EmailSearchRows is both the e-mails pane's and
// the e-mails drawer's — without a second file: the pane is in the main
// bundle already, and the lazy drawer importing from it pulls nothing new.
// The destination picker and the "one PDF" checkbox left the row (C.0, C.6):
// the drawer's two save buttons carry the destination, and one PDF is what
// saving means there. The chosen file is held as a `File` in state, not read
// off the input at the click, because the drawer receives it.
//
// `import React` is here for `react/react-in-jsx-scope` (166's remedy): the
// row adds elements to this file, and React in scope is the rule's own cure.

import React from "react";
import { lazy, Suspense, useState } from "react";
import type { CSSProperties, Dispatch } from "react";
import { Upload, FileText } from "lucide-react"; // 196 §B.2 — the file control shows a DOCUMENT (the tree's icon for one), not an attachment
import { useTranslation } from "../i18n";
import { usePermissions } from "../hooks/usePermissions";
import { RAD, FS } from "../lib/tokens";
import { PANE_TITLE, PANE_BODY } from "./CaseDocumentsSection"; // 196 §B.1 — the Documents pane's own dress, imported
import type { Translate } from "../lib/interestRefusal";
import type { WhatsappDestination, WhatsappRateField } from "../lib/whatsappRates";

// 194D §E — the messages drawer is lazy, as the e-mails drawer is: the pane
// renders on every case page and must not carry the drawer's list, its rate
// fields and its acts into the main bundle. Loaded the first time Importer is
// pressed.
const MessagesDrawer = lazy(() => import("./MessagesDrawer"));

export type WhatsappDirection = "in" | "out";

export type WhatsappRow = {
  key: string;
  timestamp: string;
  date: string;
  time: string;
  sender: string;
  direction: WhatsappDirection;
  body: string;
  // 108-d — the file the message carried, by name, and the body without its
  // marker. `wordCount` is over the CAPTION, so the caption is what the row
  // shows: printing the raw `IMG-….jpg (fichier joint)` beside a count that
  // excludes it would read as a miscount.
  attachment: string | null;
  caption: string;
  wordCount: number;
  minutesIn: number;
  minutesOut: number;
  // 194D E.4 — both directions PRICED at the viewer's own rate (194 C.2);
  // null when the matter has no rate.
  amountIn: number | null;
  amountOut: number | null;
  billed: boolean;
};

export type WhatsappArchiveResult = {
  ok: boolean;
  code?: string;
  /** 206E addendum §A4 — the route's own sentence for a refusal (the fallback for a code the drawer has no sentence of its own for) */
  error?: string;
  /** 206E addendum §A5 — how many messages the saved PDF holds (the whole conversation) */
  messageCount?: number;
  destLabel?: string;
  pdfName?: string;
  pdfDuplicate?: boolean;
  originalName?: string;
  originalOk?: boolean;
  media?: { name: string; ok: boolean; duplicate: boolean }[];
};

/** 197 C.2 — one person of the matter's Parties, as `GET …/whatsapp/parties`
 *  serves them: `ref` is `<junction>:<id>`, `role` the slot code the browser
 *  translates (`partyRoleWord`). */
export interface WhatsappParty { ref: string; name: string; role: string }

export type WhatsappImportResult = {
  format: "android" | "ios";
  dateOrder: "day-first" | "month-first";
  dateOrderMeasured: boolean;
  senders: string[];
  otherParty: string | null;
  derivedOtherParty: string | null;
  otherPartyResolved: boolean;
  destination: WhatsappDestination;
  defaultDestination: WhatsappDestination;
  freeWords: number;
  minimumMinutes: number;
  /** 194D E.3 — the six as they applied (the firm's under the drawer's overrides) */
  rates: Record<WhatsappRateField, number>;
  /** 194D E.4 — the viewer's own rate on the matter; null when it cannot be rated */
  rate: number | null;
  /** 194D E.2 — the window the counts are of ("" = open) */
  window: { from: string; to: string };
  totals: {
    messages: number;
    billable: number;
    belowFloor: number;
    outsideWindow: number;
    systemLines: number;
    media: number;
    zeroWord: number;
  };
  messages: WhatsappRow[];
  archive: WhatsappArchiveResult | null;
  /** 197 C.2 — the formal interlocutor the import was posted with, as the
   *  matter knows it; null until one is named */
  interlocutor: WhatsappParty | null;
};

/* 194D §C — the pane label, at module scope so the row component reads it. */
export const PANE_LABEL_STYLE: CSSProperties = {
  fontSize: FS.s10,
  fontWeight: 700,
  color: "var(--ts)",
  textTransform: "uppercase",
  letterSpacing: ".4px",
  whiteSpace: "nowrap",
};
/* The row: ONE GRID — title · the file field (minmax(140px, 1fr)) · Importer —
   the e-mails rows' construction (EmailSearchRows), so the two panes read as
   one section. */
const IMPORT_ROW_STYLE: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "auto minmax(140px, 1fr) auto",
  columnGap: 6,
  alignItems: "center",
  padding: "8px 10px",
  border: "1px solid var(--br)",
  borderRadius: RAD.r8,
  background: "var(--bd)",
};

interface WhatsappImportRowProps {
  t: Translate;
  /** the chosen export, or none */
  file: File | null;
  /** a file was picked (or the picker was cleared) */
  onFile: Dispatch<File | null>;
  /** Importer pressed with a file held */
  onImport: () => void;
  /** an import is running: the button waits */
  busy: boolean;
}

/** 194D §C — the import row, rendered by the pane (§C) and by the messages
 *  drawer's first row (§E.1). The file field IS the browse control (C.2–C.4):
 *  a label wrapping the hidden input, reading « Désigner le fichier » until a
 *  file is chosen and then its name with its suffix, cut at the END; the
 *  accepted formats are its hover (C.3, the input's own `accept`). Importer is
 *  the row's last track, on the right edge (C.5), and keeps its own height
 *  (A.0). */
export function WhatsappImportRow({ t, file, onFile, onImport, busy }: Readonly<WhatsappImportRowProps>) {
  return (
    <div style={IMPORT_ROW_STYLE}>
      <span style={PANE_LABEL_STYLE}>{t("whatsapp.pane_import_file", { fallback: "Import from file" })}</span>
      <label
        className="field-input commField"
        style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, cursor: "pointer" }}
        title={t("whatsapp.file_formats_title", { fallback: "Accepted formats: a WhatsApp export, .txt or .zip" })}
      >
        {/* 196 §B.2 [RULED, Laurent 2026-09-19] — a paperclip means ATTACHMENT and
            is not what this control does; it points at a file. `FileText` is the
            icon the tree already gives a document (54 sites: the linked-document
            anchor, « Facturer des documents », the billing modal's pdf/text kinds,
            the library's PDF) — taken, not invented. ONE site: the drawer's row 1
            is this same component (194D §0.5), so both surfaces move together. */}
        <FileText size={12} style={{ flexShrink: 0, color: "var(--tm)" }} />
        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: file ? "var(--tp)" : "var(--tm)" }}>
          {file ? file.name : t("whatsapp.browse_file", { fallback: "Point to file" })}
        </span>
        <input
          type="file"
          accept=".txt,.zip,text/plain,application/zip"
          style={{ display: "none" }}
          onChange={(e) => {
            onFile(e.target.files?.[0] ?? null);
            // so choosing the same file again fires onChange again
            e.target.value = "";
          }}
        />
      </label>
      <button
        type="button"
        className="btn-unified btn-unified-primary"
        disabled={busy || !file}
        onClick={() => {
          onImport();
        }}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, justifySelf: "end" }}
        title={
          file
            ? t("whatsapp.import_title", { fallback: "Read this export" })
            : t("whatsapp.import_disabled_title", { fallback: "Choose a WhatsApp export first" })
        }
      >
        <Upload size={12} />
        {busy ? t("whatsapp.importing", { fallback: "Importing…" }) : t("whatsapp.import_button", { fallback: "Import" })}
      </button>
    </div>
  );
}

export default function WhatsappImportPane({
  kind,
  cs,
  className,
  style,
  toast,
  firmSettings = null,
}: Readonly<{
  kind: "case" | "cc";
  cs: { id: number };
  /** 198 §C — the firm settings object for the messages drawer's period picker (D.3) */
  firmSettings?: unknown;
  /* 194 B.0 — optional: the card is mounted inside the Communication section
     (CaseEmailsPane) now, with no grid slot of its own. */
  className?: string;
  style?: Record<string, unknown>;
  toast?: (message: string, variant?: string) => void;
}>) {
  /* 181 A.3 — `t` narrowed at the seam (the house pattern, InvoiceTable.tsx). */
  const { t } = useTranslation() as { t: Translate };
  const { can } = usePermissions();
  const scopeArg = kind === "cc" ? { ccId: cs.id } : { caseId: cs.id };
  // 108-a — the card's OWN rights, not the email card's. It rode on
  // caseView.emails.* until then, which handed WhatsApp billing to everyone
  // holding email billing without anybody granting it. Both start ungranted.
  const canBill = can("caseView.whatsapp.billing", scopeArg);
  const canArchive = can("caseView.whatsapp.archive", scopeArg);

  const urlBase =
    kind === "cc"
      ? `/collective-cases/${String(cs.id)}/whatsapp`
      : `/cases/${String(cs.id)}/whatsapp`;

  // 194D §C — the chosen export, held as a File (the drawer receives it).
  const [file, setFile] = useState<File | null>(null);
  /* 194D §C.0 / §E — the drawer, opened by Importer with the file the row
     holds; `nonce` remounts it per press so it opens fresh on that import. */
  const [drawer, setDrawer] = useState<{ file: File; nonce: number } | null>(null);

  return (
    /* 196 §B.1 [RULED, Laurent 2026-09-19] — THE PANE WEARS THE DOCUMENTS
       SECTION'S DRESS: `.detail-card` + `<h4 style={PANE_TITLE}>` +
       `<div style={PANE_BODY}>`, CaseDocumentsSection's own idiom imported
       (« DOSSIER DE LA CAUSE »'s capitals and size), the host's optional
       class beside it; the inline card and the bare `<h3>` that stood here
       were this screen's own spacing. */
    <div className={className ? `detail-card ${className}` : "detail-card"} style={style}>
      <h4 style={PANE_TITLE}>
        {/* 194 B.0 — the second pane of the Communication section. */}
        {t("communication.pane_messages", { fallback: "Messages" })}
      </h4>
      <div style={PANE_BODY}>
        {/* 194D §C — the one row; Importer opens the drawer (§E), which runs the
            import and holds everything the pane used to show (C.0). */}
        <WhatsappImportRow
          t={t}
          file={file}
          onFile={setFile}
          onImport={() => {
            if (file) setDrawer({ file, nonce: Date.now() });
          }}
          busy={false}
        />

        {drawer && (
          <Suspense fallback={null}>
            <MessagesDrawer
              key={drawer.nonce}
              urlBase={urlBase}
              initialFile={drawer.file}
              onClose={() => {
                setDrawer(null);
              }}
              canBill={canBill}
              canArchive={canArchive}
              toast={toast}
              firmSettings={firmSettings}
            />
          </Suspense>
        )}
      </div>
    </div>
  );
}
